// Who is this fax going to? One picker, used by both "Send a fax" and
// "Request from provider", in the order a coordinator actually thinks:
//
//   1. this child's care team   — the PCP, the ordering physician, the
//      neurologist. One click. Nearly every clinical fax goes to one of them.
//   2. the Provider Directory   — any physician (D###) or facility (F###) on
//      record, searched by name, NPI, practice or specialty.
//   3. typed by hand            — anyone not on record; the caller keeps its
//      own fields and its own type-it-twice confirmation.
//
// Picking hands back a filled recipient plus where it came from, so the
// caller can skip the double-entry check for a number that is already on
// record and can record the care role on the fax. It also hands back the
// records fax (directory_fax_routes): where a records request to that doctor
// or site goes — for every CHOA entry, CHOA's Health Information Services.
import { useEffect, useMemo, useRef, useState } from 'react';
import { getCareTeamDetailed, listPhysicians, listFacilities, listFaxRoutes, faxRouteIndex, faxFor, searchPhysicians, physicianName, fmtPhone, CARE_ROLE_LABEL } from '../../lib/directory';

const RefCode = ({ code }) => code ? (
  <span style={{ fontFamily: 'monospace', fontSize: 10.5, fontWeight: 600, color: 'var(--accent)', background: 'var(--accent-bg)', border: '1px solid var(--accent-border)', borderRadius: 4, padding: '1px 5px' }}>{code}</span>
) : null;

const RoleBadge = ({ role }) => (
  <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text3)', border: '1px solid var(--border2)', borderRadius: 4, padding: '1px 5px' }}>
    {CARE_ROLE_LABEL[role] || role}
  </span>
);

// A care role maps onto the letter's notion of who it is addressed to.
export const roleToKind = (role) => (role === 'pcp' || role === 'ordering') ? role : 'other';

const fromPhysician = (p, role, route) => ({
  name: p.display_name || physicianName(p),
  practice: p.facility_name || '',
  fax: p.fax || '',
  recordsFax: route?.records_fax || '',
  organisation: route?.organisation_name || '',
  phone: p.phone || '',
  kind: roleToKind(role),
  source: { type: 'physician', id: p.id, ref_code: p.ref_code, role: role || null },
});

const fromFacility = (f, route) => ({
  name: f.name,
  practice: '',
  fax: faxFor(route) || f.fax || '',
  recordsFax: route?.records_fax || '',
  organisation: route?.organisation_name || '',
  phone: f.phone || '',
  kind: 'other',
  source: { type: 'facility', id: f.id, ref_code: f.ref_code, role: null },
});

export default function FaxRecipientPicker({ client, onPick, chosen, onClear, autoFocus = false }) {
  const [team, setTeam] = useState(null);
  const [physicians, setPhysicians] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [routes, setRoutes] = useState(() => new Map());
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  useEffect(() => {
    listPhysicians().then(({ data }) => setPhysicians((data || []).filter(p => p.status === 'active')));
    listFacilities().then(({ data }) => setFacilities((data || []).filter(f => f.status === 'active')));
    listFaxRoutes().then(({ data }) => setRoutes(faxRouteIndex(data)));
  }, []);
  const route = (kind, id) => routes.get(`${kind}:${id}`);
  useEffect(() => {
    if (!client?.id) { setTeam(null); return; }
    getCareTeamDetailed(client.id).then(({ data }) => setTeam((data || []).filter(l => l.physician)));
  }, [client?.id]);

  useEffect(() => {
    const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const results = useMemo(() => {
    const term = q.trim();
    if (term.length < 2) return [];
    const docs = searchPhysicians(physicians, term).slice(0, 6).map(p => ({ kind: 'physician', row: p }));
    const t = term.toLowerCase();
    const facs = facilities.filter(f => `${f.name} ${f.ref_code || ''} ${f.city || ''}`.toLowerCase().includes(t)).slice(0, 4).map(f => ({ kind: 'facility', row: f }));
    return [...docs, ...facs];
  }, [q, physicians, facilities]);

  const take = (r) => { onPick(r); setQ(''); setOpen(false); };

  if (chosen) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--success-bg)', border: '1px solid var(--success-border, var(--border))', borderRadius: 8, padding: '8px 10px', fontSize: 13, flexWrap: 'wrap' }}>
        <RefCode code={chosen.ref_code} />
        <b>{chosen.name}</b>
        {chosen.role && <RoleBadge role={chosen.role} />}
        {chosen.practice && <span style={{ color: 'var(--text3)' }}>· {chosen.practice}</span>}
        {chosen.fax && <span style={{ color: 'var(--text3)', fontFamily: 'monospace' }}>· {fmtPhone(chosen.fax)}</span>}
        <button type="button" onClick={onClear} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 12.5, fontWeight: 500, fontFamily: 'inherit' }}>Change</button>
      </div>
    );
  }

  const teamWithFax = (team || []).filter(l => l.physician?.fax);
  const teamNoFax = (team || []).filter(l => !l.physician?.fax);

  return (
    <div ref={box} style={{ position: 'relative' }}>
      {client && team !== null && (
        teamWithFax.length > 0 ? (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11.5, color: 'var(--text3)', marginBottom: 4 }}>{client.first_name}'s care team</div>
            <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              {teamWithFax.map(l => (
                <button key={l.id} type="button" onClick={() => take(fromPhysician(l.physician, l.role, route('physician', l.physician.id)))}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', font: 'inherit', fontSize: 13, color: 'var(--text)', background: 'var(--card-bg)', border: 'none', borderBottom: '1px solid var(--border)', padding: '7px 10px', cursor: 'pointer' }}>
                  <RoleBadge role={l.role} />
                  <span style={{ fontWeight: 600 }}>{l.physician.display_name || physicianName(l.physician)}</span>
                  {l.physician.specialty && <span style={{ color: 'var(--text3)', fontSize: 12 }}>{l.physician.specialty}</span>}
                  <span style={{ marginLeft: 'auto', color: 'var(--text3)', fontFamily: 'monospace', fontSize: 12 }}>{fmtPhone(l.physician.fax)}</span>
                </button>
              ))}
            </div>
            {teamNoFax.length > 0 && (
              <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 4 }}>
                No fax on record for {teamNoFax.map(l => l.physician.display_name || physicianName(l.physician)).join(', ')} — add it in General Info → Physicians.
              </div>
            )}
          </div>
        ) : (
          <div style={{ fontSize: 11.5, color: 'var(--text3)', marginBottom: 8 }}>
            No care team on file for {client.first_name} yet — add one on the client's Care Team, and they will be one click away here.
          </div>
        )
      )}

      <input className="form-input" value={q} autoFocus={autoFocus}
        placeholder="Search any physician or facility on record…"
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} />

      {open && q.trim().length >= 2 && (
        <div style={{ position: 'absolute', zIndex: 30, left: 0, right: 0, marginTop: 4, background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow)', maxHeight: 260, overflow: 'auto' }}>
          {results.length === 0 && <div style={{ padding: '8px 10px', fontSize: 12.5, color: 'var(--text3)' }}>Nothing on record matches — type the details in below.</div>}
          {results.map(({ kind, row }) => (
            <button key={`${kind}_${row.id}`} type="button"
              onClick={() => take(kind === 'physician' ? fromPhysician(row, null, route('physician', row.id)) : fromFacility(row, route('facility', row.id)))}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', font: 'inherit', fontSize: 13, color: 'var(--text)', background: 'none', border: 'none', borderBottom: '1px solid var(--border)', padding: '7px 10px', cursor: 'pointer' }}>
              <RefCode code={row.ref_code} />
              <span style={{ fontWeight: 600 }}>{kind === 'physician' ? (row.display_name || physicianName(row)) : row.name}</span>
              <span style={{ color: 'var(--text3)', fontSize: 12, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {kind === 'physician' ? [row.specialty, row.facility_name].filter(Boolean).join(' · ') : [row.city, row.state].filter(Boolean).join(', ')}
              </span>
              {(() => {
                const fax = kind === 'physician' ? row.fax : (faxFor(route('facility', row.id)) || row.fax);
                return (
                  <span style={{ marginLeft: 'auto', color: fax ? 'var(--text3)' : 'var(--warn)', fontFamily: 'monospace', fontSize: 12, flexShrink: 0 }}>
                    {fax ? fmtPhone(fax) : 'no fax'}
                  </span>
                );
              })()}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
